import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Plus, Edit2, Trash2, GitBranch, UserCheck, CheckCircle, XCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { isoToAfghan } from '../utils/dateUtils';
import {
  Card, Button, SearchBox, StatusBadge, Breadcrumb, PageHeader,
  Modal, ConfirmDeleteModal, DataTable, Input, Select, FormRow, Badge,
} from '../components/common';
import api from '../services/api';
import useLang from '../i18n';

const ROLES = [
  { value: 'admin',      label: 'مدیر',       color: 'red'   },
  { value: 'accountant', label: 'حسابدار',    color: 'blue'  },
  { value: 'cashier',    label: 'صندوقدار',   color: 'green' },
  { value: 'viewer',     label: 'مشاهده‌گر',  color: 'gray'  },
];

// ── BRANCHES ─────────────────────────────────────────────
export function Branches() {
  const { t } = useLang();
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem]   = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);

  const { data, isLoading, refetch } = useQuery(
    'branches',
    () => api.get('/branches', { params: { limit: 50 } }),
    { staleTime: 0, refetchOnMount: true }
  );
  const branches = data?.data || [];

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();

  const createMutation = useMutation(
    (b) => api.post('/branches', b),
    { onSuccess: () => { qc.invalidateQueries('branches'); qc.invalidateQueries('my-branches'); setShowModal(false); reset(); toast.success(t('addSuccess')); refetch(); } }
  );
  const updateMutation = useMutation(
    ({ id, ...b }) => api.put(`/branches/${id}`, b),
    { onSuccess: () => { qc.invalidateQueries('branches'); qc.invalidateQueries('my-branches'); setShowModal(false); setEditItem(null); reset(); toast.success(t('updateSuccess')); refetch(); } }
  );
  const deleteMutation = useMutation(
    (id) => api.delete(`/branches/${id}`),
    { onSuccess: () => { qc.invalidateQueries('branches'); qc.invalidateQueries('my-branches'); setDeleteModal(null); toast.success(t('deleteSuccess')); refetch(); } }
  );
  const toggleMutation = useMutation(
    ({ id, isActive }) => api.put(`/branches/${id}`, { isActive: !isActive }),
    { onSuccess: () => { qc.invalidateQueries('branches'); qc.invalidateQueries('my-branches'); toast.success(t('updateSuccess')); refetch(); } }
  );

  const openEdit = (item) => {
    setEditItem(item);
    Object.entries(item).forEach(([k, v]) => setValue(k, v));
    setShowModal(true);
  };
  const onSubmit = (d) => {
    const branchesList = d.branches 
      ? (Array.isArray(d.branches) ? d.branches : [d.branches]).filter(Boolean)
      : [];
    
    const body = { ...d, branches: branchesList };
    editItem ? updateMutation.mutate({ id: editItem._id, ...body }) : createMutation.mutate(body);
  };
  
  const COLUMNS = [
    { key:'code', label:t('code'), render:(v)=><code className="text-xs bg-gray-100 px-2 py-0.5 rounded-lg font-mono">{v}</code> },
    { key:'name', label:t('branchName'), render:(v,r)=>(
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0">
            <GitBranch className="w-4 h-4" />
          </div>
          <div>
            <p className="text-xs font-bold text-gray-800">{v}</p>
            {r.isMain && <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold">{t('mainBranch')}</span>}
          </div>
        </div>
      )
    },
    { key:'phone',   label:t('phone'),  render:(v)=><span className="text-xs text-gray-500">{v||'—'}</span> },
    { key:'address', label:t('address'),  render:(v)=><span className="text-xs text-gray-400 truncate max-w-[160px] block">{v||'—'}</span> },
    { key:'isActive',label:t('status'),render:(v)=><StatusBadge status={v!==false?t('active'):t('inactive')}/> },
  ];

  return (
    <div>
      <Breadcrumb items={[t('management'), t('branches')]} />
      <PageHeader title={t('branchManagement')} subtitle={`${branches.length} ${t('branches')}`}
        actions={
          <Button variant="primary" icon={<Plus className="w-4 h-4"/>}
            onClick={() => { setEditItem(null); reset(); setShowModal(true); }}>
            {t('newBranch')}
          </Button>
        }
      />

      <div className="grid grid-cols-3 gap-4 mb-5">
        {[
          { label: t('totalBranches'),  value: branches.length,                              color:'text-blue-700',  bg:'bg-blue-50'  },
          { label: t('activeBranches'), value: branches.filter(b=>b.isActive!==false).length,color:'text-green-700', bg:'bg-green-50' },
          { label: t('inactiveBranches'),   value: branches.filter(b=>b.isActive===false).length,color:'text-gray-600',  bg:'bg-gray-100'  },
        ].map(s => (
          <Card key={s.label} className={`p-4 ${s.bg}`}>
            <p className="text-xs text-gray-500 mb-1">{s.label}</p>
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
          </Card>
        ))}
      </div>

      <Card>
        <DataTable columns={COLUMNS} data={branches} loading={isLoading} emptyTitle={t('noBranches')}
          actions={(row) => (
            <div className="flex gap-1">
              <button title={row.isActive!==false?t('deactivate'):t('activate')}
                className={`p-1.5 rounded transition-colors ${row.isActive!==false ? 'hover:bg-amber-50 text-amber-500' : 'hover:bg-green-50 text-green-500'}`}
                onClick={() => toggleMutation.mutate({ id: row._id, isActive: row.isActive })}>
                {row.isActive !== false ? <XCircle className="w-3.5 h-3.5"/> : <CheckCircle className="w-3.5 h-3.5"/>}
              </button>
              <button className="p-1.5 rounded hover:bg-gray-100 text-gray-400" onClick={() => openEdit(row)}>
                <Edit2 className="w-3.5 h-3.5"/>
              </button>
              {!row.isMain && (
                <button className="p-1.5 rounded hover:bg-red-50 text-red-400" onClick={() => setDeleteModal(row)}>
                  <Trash2 className="w-3.5 h-3.5"/>
                </button>
              )}
            </div>
          )}
        />
      </Card>

      <Modal isOpen={showModal} onClose={() => { setShowModal(false); setEditItem(null); reset(); }}
        title={editItem ? t('editBranch') : t('newBranch')}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <FormRow>
            <Input label={t('branchName')+' *'} placeholder={t('phBranchName')} {...register('name', { required: t('required') })} error={errors.name?.message} />
            <Input label={t('branchCode')+' *'} placeholder={t('phBranchCode')} {...register('code', { required: t('required') })} error={errors.code?.message} />
          </FormRow>
          <FormRow>
            <Input label={t('phone')} placeholder={t('phPhone')} {...register('phone')} />
            <Input label={t('managerName')} placeholder={t('phManagerName')} {...register('managerName')} />
          </FormRow>
          <Input label={t('address')} {...register('address')} />
          <div className="flex items-center gap-2">
            <input type="checkbox" id="isMain" {...register('isMain')} className="rounded" />
            <label htmlFor="isMain" className="text-xs text-gray-600">{t('isMainBranch')}</label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" onClick={() => { setShowModal(false); setEditItem(null); reset(); }}>{t("cancel")}</Button>
            <Button variant="primary" type="submit" loading={createMutation.isLoading || updateMutation.isLoading}>
              {editItem ? t('update') : t('save')}
            </Button>
          </div>
        </form>
      </Modal>

      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-base font-bold text-red-600 mb-2">{t('deleteBranch')}</h3>
            <p className="text-sm text-gray-600 mb-4">{t('deleteBranchConfirm')} <strong>{deleteModal.name}</strong>؟</p>
            <div className="flex gap-2">
              <Button type="button" onClick={() => setDeleteModal(null)} className="flex-1 justify-center">{t("cancel")}</Button>
              <Button variant="danger" loading={deleteMutation.isLoading}
                onClick={() => deleteMutation.mutate(deleteModal._id)}
                className="flex-1 justify-center">{t("delete")}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── USERS ─────────────────────────────────────────────────
export function Users() {
  const { t } = useLang();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem]   = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);

  const { data: usersData, isLoading } = useQuery(['users', search], () => api.get('/users', { params: { search, limit: 50 } }));
  const { data: branchesData } = useQuery('branches-list', () => api.get('/branches', { params: { limit: 50 } }));

  const users    = usersData?.data   || [];
  const branches = branchesData?.data || [];

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm({
    defaultValues: { role: 'accountant', isActive: true },
  });

  const createMutation = useMutation((b) => api.post('/users', b),      { onSuccess: () => { qc.invalidateQueries('users'); setShowModal(false); reset(); toast.success(t('addSuccess')); } });
  const updateMutation = useMutation(({ id, ...b }) => api.put(`/users/${id}`, b), { onSuccess: () => { qc.invalidateQueries('users'); setShowModal(false); setEditItem(null); reset(); toast.success(t('updateSuccess')); } });
  const deleteMutation = useMutation(({ id }) => api.delete(`/users/${id}`, { data: { deleteReason: t('deletedByAdmin') } }), { onSuccess: () => { qc.invalidateQueries('users'); setDeleteModal(null); toast.success(t('deleteSuccess')); } });

  const openEdit = (item) => { setEditItem(item); Object.entries(item).forEach(([k,v]) => setValue(k,v)); setShowModal(true); };
  const onSubmit = (d) => editItem ? updateMutation.mutate({ id: editItem._id, ...d }) : createMutation.mutate(d);

  const COLUMNS = [
    { key:'name', label:t('name'), render:(v,r)=>(
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-bold">
            {v?.charAt(0)||'U'}
          </div>
          <div>
            <p className="text-xs font-bold text-gray-800">{v}</p>
            <p className="text-[10px] text-gray-400">{r.email}</p>
          </div>
        </div>
      )
    },
    { key:'role',     label:t('role'),      render:(v)=>{ const r=ROLES.find(x=>x.value===v); return r?<Badge label={r.label} color={r.color}/>:null; } },
    { key:'lastLogin',label:t('lastLogin'),render:(v)=><span className="text-gray-400 text-xs">{v?isoToAfghan(v):'—'}</span> },
    { key:'isActive', label:t('status'),    render:(v)=><StatusBadge status={v!==false?t('active'):t('inactive')}/> },
  ];

  return (
    <div>
      <Breadcrumb items={[t('management'), t('users')]} />
      <PageHeader title={t('userManagement')} subtitle={`${users.length} ${t('users')}`}
        actions={<>
          <SearchBox value={search} onChange={setSearch} placeholder={t('phSearchUser')} />
          <Button variant="primary" icon={<Plus className="w-4 h-4"/>} onClick={() => { setEditItem(null); reset(); setShowModal(true); }}>{t('addUser')}</Button>
        </>}
      />
      <Card>
        <DataTable columns={COLUMNS} data={users} loading={isLoading} emptyTitle={t('noData')}
          actions={(row) => (
            <div className="flex gap-1">
              <button className="p-1.5 rounded hover:bg-gray-100 text-gray-400" onClick={() => openEdit(row)}><Edit2 className="w-3.5 h-3.5"/></button>
              <button className="p-1.5 rounded hover:bg-red-50 text-red-400" onClick={() => setDeleteModal(row)}><Trash2 className="w-3.5 h-3.5"/></button>
            </div>
          )}
        />
      </Card>

      <Modal isOpen={showModal} onClose={() => { setShowModal(false); setEditItem(null); reset(); }}
        title={editItem ? t('editUser') : t('addUser')}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <FormRow>
            <Input label={t('fullName')+' *'} {...register('name', { required: t('required') })} error={errors.name?.message} />
            <Input label={t('email')+' *'} type="email" {...register('email', { required: t('required') })} error={errors.email?.message} />
          </FormRow>
          {!editItem && (
            <Input label={t('password')+' *'} type="password"
              {...register('password', { required: t('required'), minLength: { value:6, message:t('minLengthPassword') } })}
              error={errors.password?.message} />
          )}
          <FormRow>
            <Select label={t('role')+' *'} options={ROLES.map(r=>({value:r.value,label:r.label}))} {...register('role')} />
            <Input label={t('phone')} {...register('phone')} />
          </FormRow>
          {branches.length > 0 && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">{t('branchAccess')}</label>
              <div className="grid grid-cols-2 gap-2">
                {branches.map(b => (
                  <label key={b._id} className="flex items-center gap-2 p-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input type="checkbox" value={b._id} {...register('branches')} className="rounded" />
                    <span className="text-xs text-gray-700">{b.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" onClick={() => { setShowModal(false); setEditItem(null); reset(); }}>{t("cancel")}</Button>
            <Button variant="primary" type="submit" loading={createMutation.isLoading || updateMutation.isLoading}>
              {editItem ? t('update') : t('save')}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDeleteModal isOpen={!!deleteModal} onClose={() => setDeleteModal(null)}
        onConfirm={() => deleteMutation.mutate({ id: deleteModal._id })}
        loading={deleteMutation.isLoading} itemName={deleteModal?.name} />
    </div>
  );
}